import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

// Feature Images
import DailyQuiltImg from '../assets/features/daily_quilt.png';
import ClinicalDashboardImg from '../assets/features/clinical.png';
import AdaptiveInsightsImg from '../assets/features/adaptive_insights.png';
import CompassionateCareImg from '../assets/features/compassionate_care.png';

interface Feature {
    id: string;
    title: string;
    description: string;
    image: string;
    badge: string;
    /** Badge accent, tied to the feature so it survives reordering */
    accent: 'purple' | 'blue' | 'emerald';
    color: string;
    gradient?: 'top' | 'bottom';
}

const features: Feature[] = [
    { 
        id: "feed",
        title: "Your Feed",
        description: "Your daily dose of wellness. Quilt delivers short-form videos to you daily, tailored to help you stay engaged in your well-being.",
        badge: "Engage",
        accent: "purple",
        image: DailyQuiltImg,
        color: "from-purple-500/10 to-indigo-500/10",
        gradient: "bottom"
    },
    {
        id: "adaptive",
        title: "Your Algorithm",
        description: "An algorithm for your well-being. Quilt learns your needs, goals, and preferences from every swipe and session to create content that truly matters to you.",
        badge: "Learn",
        accent: "emerald",
        image: AdaptiveInsightsImg,
        color: "from-emerald-500/10 to-teal-500/10",
        gradient: "bottom"
    },
    {
        id: "clinical",
        title: "Your Care Team",
        description: "Digital wellness with a human touch. Your healthcare provider adjusts your feed based on your progress, bridging the gap between visits.",
        badge: "Support",
        accent: "blue",
        image: ClinicalDashboardImg,
        color: "from-blue-500/10 to-cyan-500/10",
        gradient: "top"
    }
    // ,
    // { // TODO: make it about tracking your recovery or your journey, reflection?
    //     id: "care",
    //     title: "Your Journey",
    //     description: "Digital wellness that feels human. We keep you motivated and on track with content that actually resonates with your state of mind.",
    //     image: CompassionateCareImg,
    //     color: "from-pink-500/10 to-rose-500/10",
    //     gradient: "bottom"
    // }
];

const BADGE_STYLES: Record<Feature['accent'], string> = {
    purple: "bg-purple-500/10 text-purple-600 border-purple-200 dark:border-purple-900/50",
    blue: "bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-900/50",
    emerald: "bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:border-emerald-900/50",
};

const FeatureRow = ({
    feature,
    index,
}: {
    feature: Feature;
    index: number;
}) => {
    const isEven = index % 2 === 0;

    return (
        <div className="py-10 md:py-16 px-6 lg:px-12">
            <div className={cn(
                "max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center"
            )}>
                {/* Text Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className={cn(
                        "flex flex-col text-center items-center",
                        isEven ? "lg:order-2 lg:text-left lg:items-start" : "lg:order-1 lg:text-right lg:items-end"
                    )}
                >
                    <div className={cn(
                        "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6 border w-fit",
                        BADGE_STYLES[feature.accent]
                    )}>
                        {feature.badge}
                    </div>
                    <h3 className="text-4xl lg:text-5xl font-light mb-6 dark:text-white leading-tight">
                        {feature.title}
                    </h3>
                    <p className="text-lg text-slate-600 dark:text-gray-300 font-light leading-relaxed max-w-lg">
                        {feature.description}
                    </p>
                </motion.div>

                {/* Image Side */}
                <div className={cn(
                    "relative group max-w-sm mx-auto overflow-hidden",
                    isEven ? "lg:order-1" : "lg:order-2"
                )}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 100 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}

                    >

                        <img
                            src={feature.image}
                            alt={feature.title}
                            className="w-full h-auto object-cover"
                        />

                    </motion.div>
                    {/* Optional Gradient Blend */}
                    {feature.gradient && (
                        <div className={cn(
                            "absolute inset-0 pointer-events-none",
                            feature.gradient === 'bottom'
                                ? "bg-gradient-to-t from-white via-white/10 to-transparent dark:from-black dark:via-black/10"
                                : "bg-gradient-to-b from-white via-white/10 to-transparent dark:from-black dark:via-black/10"
                        )} />
                    )}
                </div>
            </div>
        </div>
    );
};

export const PatientsFeatures: React.FC<{ id?: string }> = ({ id }) => {
    return (
        <div id={id} className="relative w-full bg-white dark:bg-black overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="flex flex-col items-center text-center pt-10 md:pt-24">
                    <h2 className="text-base font-medium text-purple-500 uppercase tracking-widest mb-4">
                        Features
                    </h2>
                    <h2 className="text-5xl lg:text-7xl font-light dark:text-white mb-6">
                        How Quilt Works
                    </h2>
                </div>
            </div>

            <div className="space-y-12">
                {features.map((feature, index) => (
                    <FeatureRow
                        key={feature.id}
                        feature={feature}
                        index={index}
                    />
                ))}
            </div>
        </div>
    );
};
